import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';

import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Avatar from '@material-ui/core/Avatar';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';

import GroupSimpleSelect from 'app/components/group/SimpleSelect';
import Loading from 'app/components/loaders/Loading';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    groupLink: {
        verticalAlign: 'inherit'
    },
    subForm: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1)
    }
}));

const initValues = {
    id: 0,
    group_id: 0,
    message: '',
    commentable: true,
    voteable: true
}

export default function EditPostDialog(props) {

    const { app } = useApp();
    const api = useApi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { post, initGroups, initGroupsLoading, open, onClose, onPublish, onClosed } = props;
    const classes = useStyles();

    const [ values, setValues ] = useState(initValues);

    const [ step, setStep ] = useState(1);
    const [ groups, setGroups ] = useState([]);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ groupsLoading, setGroupsLoading ] = useState(false);

    // Getter Functions -----------------------------------------------

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/group')
            .then(res => setGroups(res.groups))
            .catch( error => {/*console.log(error);*/})
            .then(() => setGroupsLoading(false));
    };

    useEffect(() => {
        if (initGroups) {
            setGroups(initGroups);
        } else {
            getGroups();
        }
    }, [initGroups]);

    useEffect(() => {
        setGroupsLoading(initGroupsLoading);
    }, [initGroupsLoading])

    useEffect(() => {
        if (post) {
            const keys = Object.keys(initValues);
            const newValues = {};
            for (const key of keys) {
                if (post[key] !== null) {
                    newValues[key] = post[key];
                }
            }
            setValues(newValues);
        }
        setErrors({});
    }, [post]);

    const selectedGroup = useMemo(() => {
        return groups.find(group => group.id === values.group_id);
    }, [groups, values.group_id]);

    const membersGroup = useMemo(() => {
        if (groups.length) {
            return groups.find(group => group.membership_level == true && group.members_level == true && group.parent_id === 0) ?? {id: 0};
        }
        return {id: 0}
    }, [groups]);

    useEffect(() => {
        if (values.id === 0 && values.group_id === 0) {
            handleGroupId(membersGroup.id);
        }
    }, [membersGroup]);

    // Setter Functions -----------------------------------------------

    const handleMessage = e => {
        const value = e.target.value;

        if (isError('message')) delete errors.message;

        setValues({ ...values, message: value });
    };

    const handleGroupId = id => {
        if (isError('group_id')) delete errors.group_id;
        setValues({ ...values, group_id: id });
    };

    const handleStep = (e,s) => {
        e.preventDefault();
        setStep(s);
    };

    const toggleCommentable = e => {
        e.preventDefault();
        setValues({ ...values, commentable: !values.commentable });
    }

    const toggleVoteable = e => {
        e.preventDefault();
        setValues({ ...values, voteable: !values.voteable });
    }

    // Post Functions -----------------------------------------------

    const publishPost = () => {
        setLoading(true);

        api.post('/post/' + values.id, values)
            .then(() => onPublish(values))
            .catch(error => {
                console.log(error)
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    const isError = field => {
        if (errors[field]) return true;
        return false;
    };

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    };

    const onExited = () => {
        setValues({...initValues, group_id: membersGroup.id});
        setStep(1);
        setLoading(false);
        setGroupsLoading(false);
        onClosed();
    }

    return (
        <Dialog
            aria-labelledby="edit-post-dialog-title"
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            onExited={onExited}
            fullScreen={isMobile}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-post-dialog-title">
                {(step === 1) &&
                    <Fragment>
                        {(post && post.id > 0) ? 'Edit Post' : 'New Post'}
                        <IconButton
                            aria-label="Cancel"
                            className={classes.closeButton}
                            onClick={onClose}
                            disabled={loading}
                            >
                            <CloseIcon />
                        </IconButton>
                    </Fragment>
                }
                {step === 2 &&
                    <Button
                        startIcon={<LeftIcon/>}
                        aria-label="Back"
                        onClick={(e) => handleStep(e,1)}
                        >
                        Back to Post
                    </Button>
                }
            </DialogTitle>
            <DialogContent>
                {(step === 1) &&
                    <form>
                        <CardHeader
                            avatar={
                                <Avatar
                                    aria-label={app.user.full_name}
                                    alt={app.user.full_name}
                                    className={classes.avatar}
                                    src={app.user.avatar ? app.user.avatar.full_url : null}
                                    >
                                    {app.user.initials}
                                </Avatar>
                            }
                            title={app.user.full_name}
                            subheader={
                                <span>
                                    Posting to:&nbsp;
                                    <Tooltip
                                        title={values.group_id === 0 ? "Viewable to everyone in the Club" : "Viewable to " + selectedGroup.title}
                                        aria-label={values.group_id === 0 ? "Viewable to everyone in the Club" : "Viewable to " + selectedGroup.title}
                                        >
                                        <Link
                                            className={classes.groupLink}
                                            variant="subtitle2"
                                            component="button"
                                            onClick={(e) => handleStep(e,2)}
                                            color="secondary"
                                            >
                                            {values.group_id === 0 ? app.club.name : selectedGroup.title}
                                        </Link>
                                    </Tooltip>
                                </span>

                            }
                        />
                        <TextField
                            id="input-post-message"
                            label="Message"
                            name="message"
                            value={values.message}
                            onChange={handleMessage}
                            fullWidth
                            error={isError('message')}
                            helperText={getErrorMessage('message')}
                            variant="outlined"
                            multiline
                            autoFocus
                            margin="normal"
                            color="secondary"
                            rows="4"
                            rowsMax="4"
                            inputProps={{
                                maxLength: "255"
                            }}
                        />
                        <Typography variant="subtitle2" color="textSecondary" className={classes.subForm}>
                            Group members can&nbsp;
                            <Tooltip
                                title="Toggle the ability for group members to comment on your post."
                                aria-label="Toggle the ability for group members to comment on your post."
                                >
                                <Link
                                    className={classes.groupLink}
                                    variant="subtitle2"
                                    component="button"
                                    onClick={toggleCommentable}
                                    color="secondary"
                                    >
                                    {values.commentable ? 'comment' : 'not comment'}
                                </Link>
                            </Tooltip>
                            &nbsp;and can&nbsp;
                            <Tooltip
                                title="Toggle the ability for group members to vote on your post."
                                aria-label="Toggle the ability for group members to vote on your post."
                                >
                                <Link
                                    className={classes.groupLink}
                                    variant="subtitle2"
                                    component="button"
                                    onClick={toggleVoteable}
                                    color="secondary"
                                    >
                                    {values.voteable ? 'vote' : 'not vote'}
                                </Link>
                            </Tooltip>
                            &nbsp;on your post.
                        </Typography>
                    </form>
                }
                {(step === 2) &&
                    <form>
                        <Typography variant="h6">Select a Group</Typography>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            You can post to any of the groups you are a member of. Only other group members will see your post.
                        </Typography>
                        <GroupSimpleSelect
                            initSelectedId={values.group_id ?? membersGroup.id}
                            initGroupOptions={groups}
                            onSelect={handleGroupId}
                            optionsLoading={groupsLoading}
                        />

                    </form>
                }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={publishPost}
                    color="secondary"
                    disabled={loading || step === 2}
                    variant="contained"
                    fullWidth
                    >
                    {values.id > 0 ? 'Update' : 'Post'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
